import React, {useEffect, useState} from 'react';
import {useSearchParams} from "react-router-dom";
import JSZip from "jszip";
import {mediaFormatDimensions} from "../../../config/mediaFormatDimensions";
import './CreationPreview.scss'
import {Box, Button} from "@mui/material";


const IFRAME_ID = 'PLAYER_IFRAME';

const CreationPreview = () => {

    const [searchParams] = useSearchParams();
    const [hideButtons, setHideButtons] = useState(false);

    const mediaFormat = searchParams.get('mediaFormat');

    const fetchZipFileUrl = async () => {
        try {
            const response = await fetch(searchParams.get('fileUrl') || '');
            if (!response.ok) {
                throw new Error('Failed to download the file');
            }
            return await response.blob();
        } catch (error) {
            console.error('Error while downloading the file:', error);
        }
    }

    const writeIframeDocument = (files: JSZip.JSZipObject) => {
        const currentIframe = document.getElementById(IFRAME_ID) as HTMLObjectElement;
        const htmlFileName = Object.keys(files).find(fileName => fileName.endsWith('.html'));
        const iframeDoc = currentIframe.contentDocument || currentIframe?.contentWindow?.document;

        if (currentIframe && iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(replaceMediaUrlsInHtml(files[htmlFileName as keyof typeof files], files))
            iframeDoc.close();
        }
    }

    function replaceMediaUrlsInHtml(htmlContent: any, files: any) {
        return htmlContent.replace(/(src|href)=['"]?([^'"]+)['"]?/g, (match: any, attr: any, mediaPath: any) => {
            const mediaFileName = mediaPath.split('/').pop();
            if (files[mediaFileName]) {
                if (mediaFileName.endsWith('.svg')) {
                    return `${attr}="data:image/svg+xml;base64,${btoa(files[mediaFileName])}"`;
                } else if (mediaFileName.endsWith('.css')) {
                    const cssTextWithUrlsReplaced = replaceMediaUrlsInCss(files[mediaFileName], files);
                    return `${attr}="data:text/css;base64,${btoa(cssTextWithUrlsReplaced)}"`;
                } else {
                    const mediaBlob = files[mediaFileName];
                    const mediaUrl = URL.createObjectURL(mediaBlob);
                    return `${attr}="${mediaUrl}"`;
                }
            }
            return match;
        });
    }

    function replaceMediaUrlsInCss(cssText: string, files: any) {
        return cssText.replace(/url\(['"]?([^'"]+)['"]?\)/g, (match, mediaPath) => {
            const mediaFileName = mediaPath.split('/').pop();
            if (files[mediaFileName]) {
                const mediaBlob = files[mediaFileName];
                const mediaUrl = URL.createObjectURL(mediaBlob);
                return `url(${mediaUrl})`;
            }
            return match;
        });
    }

    let maxWidth = mediaFormatDimensions[mediaFormat as keyof typeof mediaFormatDimensions][0],
        outer = document.getElementById(IFRAME_ID) as HTMLObjectElement,
        maxHeight = mediaFormatDimensions[mediaFormat as keyof typeof mediaFormatDimensions][1];

    function resize() {
        const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
        if (!iframe) {
            return;
        }
        const container = document.getElementById('wrapper-outside') as HTMLDivElement | null;
        const root = document.getElementById('creation-preview-root') as HTMLDivElement | null;
        const reactRoot = document.getElementById('root') as HTMLDivElement | null;

        // Account for the toolbar row (Refresh/Nagraj/Screenshot).
        const toolbar = document.getElementById('creation-preview-toolbar');
        const toolbarHeight = toolbar?.offsetHeight ?? 0;

        const availableWidth = window.innerWidth;
        const availableHeight = Math.max(0, window.innerHeight - toolbarHeight);

        const scaleX = availableWidth / maxWidth;
        const scaleY = availableHeight / maxHeight;

        // Fit inside viewport; don't upscale above 1:1.
        const scale = Math.min(1, scaleX, scaleY);

        iframe.style.transformOrigin = 'top left';
        iframe.style.transform = `scale(${scale})`;

        // Make container match the (scaled) iframe size.
        if (container) {
            const scaledWidth = Math.round(maxWidth * scale);
            const scaledHeight = Math.round(maxHeight * scale);

            container.style.width = `${scaledWidth}px`;
            container.style.height = `${scaledHeight}px`;
            container.style.margin = '0';

            // Keep the whole preview layout exactly the same width as the iframe container.
            if (root) {
                root.style.width = `${scaledWidth}px`;
                root.style.margin = '0';
                root.style.padding = '0';
            }
            if (reactRoot) {
                reactRoot.style.width = `${scaledWidth}px`;
                reactRoot.style.margin = '0';
                reactRoot.style.padding = '0';
            }

            // Also constrain the page itself so there is no extra horizontal space.
            document.body.style.width = `${scaledWidth}px`;
            document.body.style.margin = '0';
            document.body.style.padding = '0';
            document.documentElement.style.width = `${scaledWidth}px`;
            document.documentElement.style.margin = '0';
            document.documentElement.style.padding = '0';
        }
    }

    const startScreenRecording = async () => {
        let styleElement: HTMLStyleElement | null = null;
        let iframeStyleElement: HTMLStyleElement | null = null;
        let originalIframeBorder: string | null = null;
        let originalIframeBorderRadius: string | null = null;
        try {
            setHideButtons(true);

            // Hide cursor by adding CSS to main document
            styleElement = document.createElement('style');
            styleElement.id = 'hide-cursor-style-recording';
            styleElement.textContent = '* { cursor: none !important; }';
            document.head.appendChild(styleElement);

            // Hide cursor in iframe as well + remove iframe border/radius for clean capture
            const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;
            if (iframe) {
                originalIframeBorder = iframe.style.border;
                originalIframeBorderRadius = iframe.style.borderRadius;
                iframe.style.border = 'none';
                iframe.style.borderRadius = '0';

                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc) {
                    iframeStyleElement = iframeDoc.createElement('style');
                    iframeStyleElement.id = 'hide-cursor-style-recording';
                    iframeStyleElement.textContent = '* { cursor: none !important; }';
                    iframeDoc.head.appendChild(iframeStyleElement);
                }
            }

            // Wait a moment for the cursor to be hidden
            await new Promise(resolve => setTimeout(resolve, 200));

            const recorded = document.getElementById('recorded');

            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    cursor: 'never'  // Hide cursor in the recording
                } as any,
                audio: true,
                preferCurrentTab: true
            } as any);

            let chunks: any[] = [];
            let recorder = new MediaRecorder(stream);

            recorder.ondataavailable = e => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstart =  () => {
                // (document.getElementById('PLAYER_IFRAME') as any).contentWindow.location.reload();

                fetchZipFileUrl().then(async (blob) => {
                    if (!blob) {
                        return;
                    }
                    const files: any = {}
                    const zip = await JSZip.loadAsync(blob);
                    for (const fileName of Object.keys(zip.files)) {
                        const file = zip.files[fileName];
                        if (!file.dir) {
                            const content = fileName.endsWith('.svg') || fileName.endsWith('.css') || fileName.endsWith('.html')
                                ? await file.async("text")
                                : await file.async("blob");
                            files[fileName] = content;
                        }
                    }

                    writeIframeDocument(files);


                })

            }

            recorder.onstop = () => {
                // Remove the cursor hiding style from main document
                if (styleElement && styleElement.parentNode) {
                    styleElement.parentNode.removeChild(styleElement);
                }

                // Remove the cursor hiding style from iframe
                if (iframeStyleElement && iframeStyleElement.parentNode) {
                    iframeStyleElement.parentNode.removeChild(iframeStyleElement);
                }

                // Restore iframe border/radius (even if we couldn't inject iframe CSS)
                const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;
                if (iframe) {
                    if (originalIframeBorder !== null) {
                        iframe.style.border = originalIframeBorder;
                    }
                    if (originalIframeBorderRadius !== null) {
                        iframe.style.borderRadius = originalIframeBorderRadius;
                    }
                }

                setHideButtons(false);

                const blob = new Blob(chunks, {type: 'video/webm'});
                const url = URL.createObjectURL(blob);

                // Preview video
                (recorded as any).src = url;
                (recorded as any).play();

                // Auto-download
                const a = document.createElement('a');
                a.href = url;
                a.download = `recording_${Date.now()}.webm`;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            };



            setTimeout(function () {
                recorder.start();
            }, 200)


        } catch (err) {
            console.error('Error accessing display media:', err);
            // Make sure to remove the style elements even if there's an error
            if (styleElement && styleElement.parentNode) {
                styleElement.parentNode.removeChild(styleElement);
            }
            if (iframeStyleElement && iframeStyleElement.parentNode) {
                iframeStyleElement.parentNode.removeChild(iframeStyleElement);
            }

            // Restore iframe border/radius
            const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;
            if (iframe) {
                if (originalIframeBorder !== null) {
                    iframe.style.border = originalIframeBorder;
                }
                if (originalIframeBorderRadius !== null) {
                    iframe.style.borderRadius = originalIframeBorderRadius;
                }
            }
            setHideButtons(false);
        }
    }

    const takeScreenshot = async () => {
        let styleElement: HTMLStyleElement | null = null;
        let iframeStyleElement: HTMLStyleElement | null = null;
        try {
            setHideButtons(true);

            // Hide cursor by adding CSS to main document
            styleElement = document.createElement('style');
            styleElement.id = 'hide-cursor-style';
            styleElement.textContent = '* { cursor: none !important; }';
            document.head.appendChild(styleElement);

            // Hide cursor in iframe as well
            const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;
            if (iframe) {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc) {
                    iframeStyleElement = iframeDoc.createElement('style');
                    iframeStyleElement.id = 'hide-cursor-style';
                    iframeStyleElement.textContent = '* { cursor: none !important; }';
                    iframeDoc.head.appendChild(iframeStyleElement);
                }
            }

            // Wait a moment for the cursor to be hidden
            await new Promise(resolve => setTimeout(resolve, 50));

            // Request screen capture using Screen Capture API
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    displaySurface: 'browser',
                    cursor: 'never'  // Hide cursor in the screenshot
                } as any,
                audio: false,
                preferCurrentTab: true
            } as any);

            // Create a video element to capture the stream
            const video = document.createElement('video');
            video.srcObject = stream;
            video.autoplay = true;
            video.muted = true;

            // Wait for video to be ready
            await new Promise((resolve) => {
                video.onloadedmetadata = () => {
                    video.play();
                    resolve(null);
                };
            });

            // Wait a bit more to ensure the video is fully loaded
            await new Promise(resolve => setTimeout(resolve, 100));

            // Create canvas with video dimensions
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Draw the video frame to canvas
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            }

            // Stop all tracks to end the screen capture
            stream.getTracks().forEach(track => track.stop());

            // Remove the cursor hiding style from main document
            if (styleElement && styleElement.parentNode) {
                styleElement.parentNode.removeChild(styleElement);
            }

            // Remove the cursor hiding style from iframe
            if (iframeStyleElement && iframeStyleElement.parentNode) {
                iframeStyleElement.parentNode.removeChild(iframeStyleElement);
            }

            // Convert canvas to blob and download
            canvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `screenshot_${Date.now()}.png`;
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    setHideButtons(false);
                }
            }, 'image/png');

        } catch (err) {
            console.error('Error taking screenshot:', err);
            // Make sure to remove the style elements even if there's an error
            if (styleElement && styleElement.parentNode) {
                styleElement.parentNode.removeChild(styleElement);
            }
            if (iframeStyleElement && iframeStyleElement.parentNode) {
                iframeStyleElement.parentNode.removeChild(iframeStyleElement);
            }
            setHideButtons(false);
        }
    }

    useEffect(() => {
        let cancelled = false;

        fetchZipFileUrl().then(async (blob) => {
            if (!blob || cancelled) {
                return;
            }
            const files: any = {}
            const zip = await JSZip.loadAsync(blob);
            for (const fileName of Object.keys(zip.files)) {
                const file = zip.files[fileName];
                if (!file.dir) {
                    const content = fileName.endsWith('.svg') || fileName.endsWith('.css') || fileName.endsWith('.html')
                        ? await file.async("text")
                        : await file.async("blob");
                    files[fileName] = content;
                }
            }

            writeIframeDocument(files);

            // Ensure layout is settled before scaling.
            requestAnimationFrame(() => requestAnimationFrame(resize));
        })

        window.addEventListener("resize", resize);
        // Initial fit.
        requestAnimationFrame(() => requestAnimationFrame(resize));

        return () => {
            cancelled = true;
            window.removeEventListener("resize", resize);
        }
    }, []);


    return <div id="creation-preview-root">
        <Box id="creation-preview-toolbar" sx={{position: 'absolute', zIndex: 2, display: 'flex'}} >
            <video style={{display: 'none'}} id="recorded" controls></video>

            <Button color={'primary'}
                    sx={{display: hideButtons ? 'none' : 'block'}}
                    variant={"contained"}
                    onClick={async () => {
                        (document.getElementById('PLAYER_IFRAME') as any).contentWindow.location.reload();

                        // window.location.reload()
                        fetchZipFileUrl().then(async (blob) => {
                            if (!blob) {
                                return;
                            }
                            const files: any = {}
                            const zip = await JSZip.loadAsync(blob);
                            for (const fileName of Object.keys(zip.files)) {
                                const file = zip.files[fileName];
                                if (!file.dir) {
                                    const content = fileName.endsWith('.svg') || fileName.endsWith('.css') || fileName.endsWith('.html')
                                        ? await file.async("text")
                                        : await file.async("blob");
                                    files[fileName] = content;
                                }
                            }

                            writeIframeDocument(files);


                        })
                    }}>Refresh</Button>
            <Button color={'primary'}
                    sx={{marginLeft: '10px', display: hideButtons ? 'none' : 'block'}}
                    variant={"contained"}
                    onClick={() => {
                        startScreenRecording()
                    }}>Nagraj</Button>
            <Button color={'primary'}
                    sx={{marginLeft: '10px', display: hideButtons ? 'none' : 'block'}}
                    variant={"contained"}
                    onClick={() => {
                        takeScreenshot()
                    }}>Screenshot</Button></Box>
        <div className={'CreationPreview__container'} id={'wrapper-outside'}>
            <iframe
                id={IFRAME_ID}
                height={maxHeight}
                width={maxWidth}
                style={{margin: 0}}
            >
            </iframe>
        </div>
    </div>
}


export default CreationPreview;